import React, { useState, useContext } from 'react'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Spinner from 'react-bootstrap/Spinner'
import Alert from 'react-bootstrap/Alert'
import { useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMagic, faCheck } from '@fortawesome/free-solid-svg-icons'
import { apiPost, apiDelete } from 'api'
import AuthContext from 'context/auth-context'
import UserContext from 'context/user-context'
import AppBadge from 'shared/badges/app-badge'
import Search from 'shared/forms/search'
import { useInterpretView } from 'api/hooks/use-interpret-view'
import useSections from 'api/hooks/use-sections'
import { useVendors } from 'api/hooks/use-vendors'
import { useEmployees } from 'api/hooks/use-employees'
import { useBulkCreateDashboardVendors } from 'api/hooks/use-dashboard-vendors'
import { useBulkCreateDashboardEmployees } from 'api/hooks/use-dashboard-employees'
import { useCreateReportDataset } from 'api/hooks/use-report-datasets'
import { isVendorReference, buildDashboardParams, buildEmployeeRecords, mergeResolvedOptions } from './view-create-payload'
import OptionResolver from './option-resolver'

// Inline picker that resolves a reference_search term (vendor or employee) to concrete records.
// Uses the shared Search component (debounced, seeded from the AI's term) and reports the selected
// ids upward. When the picker finds no match and the reference has a sibling text column
// (reference.fallback), it offers to filter by that name field instead (e.g. a PCard cardholder
// who isn't a payroll employee), reported up via onTextFallback as a basic contains filter.
function ReferenceResolver({ reference, onSelectionChange, onTextFallback }) {
  const isVendor = isVendorReference(reference)
  const [searchParams, setSearchParams] = useState({ search: reference.search_term || '' })
  const [selected, setSelected] = useState([])
  const [useFallback, setUseFallback] = useState(false)

  const term = (searchParams.search || '').trim()
  const enabled = term.length >= 2
  const vendorQuery = useVendors({
    params: { search: searchParams.search, perPage: 8 },
    options: { enabled: isVendor && enabled, keepPreviousData: true }
  })
  // Employee search is municipality-wide — the employees endpoint does not filter by section, so
  // we don't pass a section param (it would be silently ignored and imply scoping that isn't there).
  const employeeQuery = useEmployees({
    params: { search: searchParams.search, perPage: 8 },
    options: { enabled: !isVendor && enabled, keepPreviousData: true }
  })

  const query = isVendor ? vendorQuery : employeeQuery
  const results = query.data?.data || query.data || []
  const noMatches = !query.isFetching && enabled && results.length === 0

  const toggle = (id) => {
    const next = selected.includes(id) ? selected.filter((s) => s !== id) : [...selected, id]
    setSelected(next)
    onSelectionChange(next)
    // Picking a real record supersedes the text-field fallback.
    if (useFallback) {
      setUseFallback(false)
      onTextFallback(null)
    }
  }

  const toggleFallback = (on) => {
    setUseFallback(on)
    onTextFallback(on ? { accessor_key: reference.fallback.accessor_key, value: term } : null)
    if (on && selected.length) {
      setSelected([])
      onSelectionChange([])
    }
  }

  return (
    <div className="border rounded-3 p-2 mt-2" style={{ backgroundColor: '#fff' }}>
      <div className="text-muted mb-1" style={{ fontSize: '0.72rem' }}>
        {isVendor ? 'Vendor filter' : 'Employee filter'} — pick the {isVendor ? 'vendor(s)' : 'employee(s)'} to include
      </div>
      <div className="mb-2">
        <Search
          params={searchParams}
          setParams={setSearchParams}
          placeholder={isVendor ? 'Search vendors…' : 'Search employees…'}
        />
      </div>
      {query.isFetching && <Spinner animation="border" size="sm" variant="secondary" />}
      {noMatches && (
        <div className="small">
          <div className="text-muted mb-1">No matching {isVendor ? 'vendor' : 'employee'} found.</div>
          {reference.fallback && (
            <Form.Check
              type="checkbox"
              id={`fallback-${reference.accessor_key}`}
              checked={useFallback}
              onChange={(e) => toggleFallback(e.target.checked)}
              label={(
                <span style={{ fontSize: '0.82rem' }}>
                  Could not find that {isVendor ? 'vendor' : 'employee'}. Filter by{' '}
                  <strong>{reference.fallback.display_name}</strong> containing “{term}” instead?
                </span>
              )}
            />
          )}
        </div>
      )}
      <div className="d-flex flex-column gap-1" style={{ maxHeight: 160, overflowY: 'auto' }}>
        {results.map((r) => {
          const label = isVendor ? r.name : (r.display_name || r.full_name)
          return (
            <Form.Check
              key={r.id}
              type="checkbox"
              id={`ref-${r.id}`}
              checked={selected.includes(r.id)}
              onChange={() => toggle(r.id)}
              label={<span style={{ fontSize: '0.82rem' }}>{label}</span>}
            />
          )
        })}
      </div>
    </div>
  )
}

// Lets the user describe a data source in natural language; interprets it (section + filters),
// shows a confirmation, then creates the View and links it to the report. Self-gates on beta.
function AiViewCreator({ reportId, sectionsList, onCreated }) {
  const user = useContext(UserContext)
  const userId = user.id
  const aiEnabled = user?.municipality?.setting?.ai_search_enabled ?? false
  const token = useContext(AuthContext)
  const queryClient = useQueryClient()

  const [stage, setStage] = useState('idle') // idle | confirming | creating
  const [query, setQuery] = useState('')
  const [interpretation, setInterpretation] = useState(null)
  const [name, setName] = useState('')
  const [sectionId, setSectionId] = useState(null)
  const [error, setError] = useState(null)
  const [vendorIds, setVendorIds] = useState([])
  const [employeeIds, setEmployeeIds] = useState([])
  // Fallback when the reference can't be resolved to a record: a basic text filter
  // ({ accessor_key, value }) merged into the dashboard params on create.
  const [textFilter, setTextFilter] = useState(null)
  // Option-resolution picks for select/multiselect/mcc_select terms that matched multiple options,
  // keyed by "accessor_key::search_term" so multiple ambiguous terms on one column stay independent.
  const [resolvedOptions, setResolvedOptions] = useState({})

  const { data: sections = sectionsList || [] } = useSections({ options: { enabled: !sectionsList } })
  const sectionOptions = sectionsList || sections

  const { mutateAsync: interpretAsync, isLoading: isInterpreting } = useInterpretView()
  const { mutateAsync: bulkVendorsAsync } = useBulkCreateDashboardVendors({ options: {} })
  const { mutateAsync: bulkEmployeesAsync } = useBulkCreateDashboardEmployees({ options: {} })
  const { mutateAsync: linkDatasetAsync } = useCreateReportDataset({ reportId, options: {} })

  if (!aiEnabled) return null

  const resetReferences = () => {
    setVendorIds([])
    setEmployeeIds([])
    setTextFilter(null)
    setResolvedOptions({})
  }

  const applyInterpretation = (data) => {
    if (data?.error) {
      setError(data.error)
      return
    }
    setError(null)
    setInterpretation(data)
    setName(data.suggested_name || '')
    setSectionId(data.section?.id ?? null)
    resetReferences()
    setStage('confirming')
  }

  const handleGenerate = async () => {
    if (!query.trim()) return
    setError(null)
    try {
      const data = await interpretAsync({ query: query.trim() })
      applyInterpretation(data)
    } catch {
      setError('Something went wrong interpreting that. Please try again.')
    }
  }

  const handleSectionChange = async (newSectionId) => {
    setSectionId(newSectionId)
    resetReferences()
    try {
      const data = await interpretAsync({ query: query.trim(), sectionId: newSectionId })
      if (data?.error) { setError(data.error); return }
      setInterpretation(data)
      setError(null)
    } catch {
      setError('Something went wrong re-interpreting for that section. Please try again.')
    }
  }

  const reset = () => {
    setStage('idle')
    setQuery('')
    setInterpretation(null)
    setName('')
    setSectionId(null)
    setError(null)
    resetReferences()
  }

  const handleCreate = async () => {
    if (!name.trim() || !sectionId) return
    setStage('creating')
    // Track the created dashboard so we can roll it back on any later failure — this client-side
    // sequence isn't transactional, and without cleanup a partial failure would leave an orphan,
    // unlinked View, and each retry would create another duplicate.
    let createdViewId = null
    try {
      // Merge the text-field fallback (if the user chose it) into the dashboard params as a
      // basic "contains" filter on the name column, then merge any option-resolution picks
      // (select/multiselect/mcc_select disambiguation) into their accessor keys.
      const baseFilters = { ...(interpretation.filters || {}) }
      if (textFilter) {
        baseFilters[textFilter.accessor_key] = textFilter.value
        baseFilters[`${textFilter.accessor_key}Operator`] = 'contains'
      }
      const filters = mergeResolvedOptions(baseFilters, Object.values(resolvedOptions))

      const view = await apiPost({
        url: `users/${userId}/dashboards`,
        token,
        objectName: 'dashboard',
        params: buildDashboardParams({ sectionId, name, filters })
      })
      createdViewId = view.id

      for (const a of interpretation.analytics || []) {
        await apiPost({
          url: `dashboards/${view.id}/dashboard_analytics`,
          token,
          objectName: 'dashboard_analytic',
          params: { analytic_id: a.analytic_id, options: a.options || 'include' }
        })
      }

      if (vendorIds.length) {
        await bulkVendorsAsync({ dashboardId: view.id, vendorIds, options: 'include' })
      }
      if (employeeIds.length) {
        const records = buildEmployeeRecords(employeeIds, interpretation.reference_search?.database_column_name)
        await bulkEmployeesAsync({ dashboardId: view.id, employees: records, options: 'include' })
      }

      await linkDatasetAsync({ dashboard_id: view.id })
      queryClient.invalidateQueries(['dashboards'])
      toast.success(`Created and linked "${name.trim()}"`)
      reset()
      onCreated?.()
    } catch {
      // Roll back the partial View (cascades to its analytics/vendors/employees) so nothing
      // orphaned lingers and a retry starts clean.
      if (createdViewId) {
        try {
          await apiDelete({ url: `dashboards/${createdViewId}`, token })
        } catch {
          // Best-effort cleanup; if it fails the View remains in the user's views list.
        }
      }
      queryClient.invalidateQueries(['dashboards'])
      setError('Could not create the view. Please try again.')
      setStage('confirming')
    }
  }

  const reference = interpretation?.reference_search
  const referenceIsVendor = reference && isVendorReference(reference)

  return (
    <div className="rounded-3 border p-3 mb-3" style={{ backgroundColor: '#f8f9fa' }}>
      <div className="d-flex align-items-center gap-2 mb-2">
        <FontAwesomeIcon icon={faMagic} className="text-primary" />
        <span className="fw-semibold" style={{ fontSize: '0.9rem' }}>Create a new data source</span>
        <AppBadge variant="ai" pill>AI</AppBadge>
      </div>

      {error && <Alert variant="danger" className="py-2 px-3 small mb-2">{error}</Alert>}

      {stage !== 'confirming' && (
        <div className="d-flex gap-2">
          <Form.Control
            as="textarea"
            rows={1}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder='e.g. "PCard charges over $1k at restricted vendors last quarter"'
            style={{ resize: 'none', fontSize: '0.85rem' }}
            disabled={isInterpreting || stage === 'creating'}
          />
          <Button
            variant="primary"
            className="flex-shrink-0"
            onClick={handleGenerate}
            disabled={!query.trim() || isInterpreting || stage === 'creating'}
          >
            {isInterpreting ? <Spinner animation="border" size="sm" /> : 'Generate'}
          </Button>
        </div>
      )}

      {stage === 'confirming' && interpretation && (
        <div>
          <div className="row g-2 mb-2">
            <div className="col-12 col-md-5">
              <Form.Label className="small text-muted mb-1">Section</Form.Label>
              <Form.Select
                size="sm"
                value={sectionId || ''}
                onChange={(e) => handleSectionChange(e.target.value)}
                disabled={isInterpreting}
              >
                {sectionOptions.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </Form.Select>
            </div>
            <div className="col-12 col-md-7">
              <Form.Label className="small text-muted mb-1">View name</Form.Label>
              <Form.Control
                size="sm"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="View name"
              />
            </div>
          </div>

          {isInterpreting ? (
            <div className="text-muted small d-flex align-items-center gap-2 my-2">
              <Spinner animation="border" size="sm" /> Re-interpreting for this section…
            </div>
          ) : (
            <>
              {(interpretation.no_filters || interpretation.info_request) && (
                <Alert variant="warning" className="py-2 px-3 small mb-2">
                  {interpretation.message || 'No filters were detected — this will create an unfiltered view for the section.'}
                </Alert>
              )}

              <div className="small mb-2">
                <div className="text-muted text-uppercase mb-1" style={{ fontSize: '0.68rem', letterSpacing: '0.06em' }}>
                  Filters
                </div>
                {(interpretation.summary || []).length === 0 && (interpretation.analytics || []).length === 0 ? (
                  <div className="text-muted">No filters.</div>
                ) : (
                  <ul className="mb-0 ps-3">
                    {(interpretation.summary || []).map((line, i) => <li key={`f${i}`}>{line}</li>)}
                    {(interpretation.analytics || []).map((a) => (
                      <li key={`a${a.analytic_id}`}>Analytic: {a.name}{a.options === 'exclude' ? ' (exclude)' : ''}</li>
                    ))}
                  </ul>
                )}
              </div>

              {reference && (
                <ReferenceResolver
                  key={`${reference.accessor_key}-${sectionId}`}
                  reference={reference}
                  onSelectionChange={referenceIsVendor ? setVendorIds : setEmployeeIds}
                  onTextFallback={setTextFilter}
                />
              )}

              {(interpretation.option_resolutions || []).map((res) => (
                <OptionResolver
                  key={`${res.accessor_key}-${res.search_term}-${sectionId}`}
                  resolution={res}
                  onChange={(values) => setResolvedOptions((prev) => ({
                    ...prev,
                    [`${res.accessor_key}::${res.search_term}`]: {
                      accessor_key: res.accessor_key,
                      filter_type: res.filter_type,
                      mode: res.mode,
                      values
                    }
                  }))}
                />
              ))}
            </>
          )}

          <div className="d-flex gap-2 mt-3">
            <Button variant="primary" onClick={handleCreate} disabled={!name.trim() || stage === 'creating' || isInterpreting}>
              {stage === 'creating'
                ? <><Spinner animation="border" size="sm" className="me-2" />Creating…</>
                : <><FontAwesomeIcon icon={faCheck} className="me-2" />Create view</>}
            </Button>
            {/* Back to the prompt (query preserved) to tweak and re-interpret. */}
            <Button variant="light" className="border" onClick={() => setStage('idle')} disabled={stage === 'creating' || isInterpreting}>
              Edit prompt
            </Button>
            <Button variant="light" className="border" onClick={reset} disabled={stage === 'creating'}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

export default AiViewCreator
